import { useMemo } from 'react';
import { buildFlatLineLayout } from '../../line/lineLayout';
import { stationPartCount, stationVisual } from '../../line/lineVisuals';

const MACHINE_W = 118;
const MACHINE_H = 70;

const point = (layout, key) => layout.points[key];

function SequenceEdges({ topology, layout }) {
  return (topology.cells || []).flatMap((cell) => {
    const seq = cell.robotSequence || [];
    return seq.slice(1).map((key, i) => {
      const a = point(layout, seq[i]);
      const b = point(layout, key);
      if (!a || !b) return null;
      return (
        <line
          key={`${cell.id}-robot-${i}`}
          x1={a.x} y1={a.y} x2={b.x} y2={b.y}
          stroke="rgba(139,160,181,0.22)"
          strokeWidth="2"
          strokeDasharray="7 8"
        />
      );
    });
  });
}

function RouteEdges({ topology, state, layout }) {
  // The part's path, conveyors and posts included — see LineIsoView. `route` is the fallback.
  const path = topology.routePath || topology.route || [];
  return path.slice(1).map((key, i) => {
    const fromKey = path[i];
    const a = point(layout, fromKey);
    const b = point(layout, key);
    if (!a || !b) return null;
    const wip = state?.wip?.[fromKey] ?? 0;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    return (
      <g key={`route-${fromKey}-${key}`}>
        <line
          x1={a.x} y1={a.y} x2={b.x} y2={b.y}
          stroke="rgba(168,144,96,0.82)"
          strokeWidth="4"
          markerEnd="url(#route-arrow)"
        />
        {wip > 0 && (
          <g transform={`translate(${mx} ${my})`}>
            <rect x="-24" y="-11" width="48" height="22" rx="11" fill="#0D1B2A" stroke="#A89060" />
            <text textAnchor="middle" y="4" fill="#E2E8F0" fontSize="10" fontWeight="700">WIP {wip}</text>
          </g>
        )}
      </g>
    );
  });
}

export default function LineFlatView({ topology, state, selectedKey, onSelect,
                                      showRoute = true, showRobotLinks = true }) {
  const layout = useMemo(() => buildFlatLineLayout(topology), [topology]);
  return (
    <div style={{ width: '100%', minHeight: 500, overflow: 'auto' }}>
      <svg
        role="img"
        aria-label={`Flat map of ${topology.name}`}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        style={{ width: '100%', minWidth: 900, display: 'block' }}
      >
        <defs>
          <marker id="route-arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#A89060" />
          </marker>
        </defs>

        {Object.entries(layout.cells).map(([cellId, r]) => {
          const cell = topology.cells.find((c) => c.id === cellId);
          const constrained = topology.balance?.constraintCellId === cellId;
          return (
            <g key={cellId}>
              <rect
                x={r.x} y={r.y} width={r.width} height={r.height} rx="22"
                fill="rgba(21,40,64,0.62)"
                stroke={constrained ? '#A89060' : 'rgba(139,160,181,0.2)'}
                strokeWidth={constrained ? 3 : 1.5}
              />
              <text x={r.x + 22} y={r.y + 30} fill="#8BA0B5" fontSize="12" fontWeight="800" letterSpacing="2">
                {cell?.name?.toUpperCase() || cellId}
              </text>
              <text x={r.x + 22} y={r.y + 49} fill="#4A5568" fontSize="10">
                {cell?.piecesPerHour ?? '—'} pcs/h · {cell?.cycleSec?.toFixed?.(1) ?? cell?.cycleSec ?? '—'} s
              </text>
            </g>
          );
        })}

        {showRobotLinks && <SequenceEdges topology={topology} layout={layout} />}
        {showRoute && <RouteEdges topology={topology} state={state} layout={layout} />}

        {(topology.nodes || []).map((node) => {
          const p = point(layout, node.key);
          if (!p) return null;
          const selected = selectedKey === node.key;
          const conveyor = node.kind === 'conveyor';
          return (
            <g key={node.key} transform={`translate(${p.x} ${p.y})`} onClick={() => onSelect(node.key)} style={{ cursor: 'pointer' }}>
              <rect
                x={conveyor ? -39 : -27} y="-18"
                width={conveyor ? 78 : 54} height="36" rx={conveyor ? 7 : 18}
                fill="#1C3557"
                stroke={selected ? '#E2E8F0' : 'rgba(139,160,181,0.45)'}
                strokeWidth={selected ? 3 : 1.5}
              />
              <text textAnchor="middle" y="4" fill="#8BA0B5" fontSize="8.5" fontWeight="700">
                {node.kind === 'conveyor' ? 'CONVEYOR' : 'POST'}
              </text>
            </g>
          );
        })}

        {(topology.stations || []).map((station) => {
          const p = point(layout, station.key);
          if (!p) return null;
          const stationState = state?.stations?.[station.key];
          const visual = stationVisual(stationState);
          const selected = selectedKey === station.key;
          const qualityTarget = (state?.quality?.stationsAwaitingApproval || []).includes(station.key);
          const wip = state?.wip?.[station.key] ?? 0;
          const parts = stationPartCount(stationState);
          return (
            <g
              key={station.key}
              transform={`translate(${p.x} ${p.y})`}
              onClick={() => onSelect(station.key)}
              style={{ cursor: 'pointer' }}
              opacity={visual.present ? 1 : 0.55}
            >
              <rect
                x={-MACHINE_W / 2} y={-MACHINE_H / 2}
                width={MACHINE_W} height={MACHINE_H} rx="10"
                fill="#152840"
                stroke={selected ? '#E2E8F0' : qualityTarget ? '#F59E0B' : visual.hex}
                strokeWidth={selected ? 4 : qualityTarget ? 4 : 2.5}
              />
              <rect x={-MACHINE_W / 2} y={-MACHINE_H / 2} width={MACHINE_W} height="5" rx="3" fill={visual.hex} />
              <text textAnchor="middle" y="-10" fill="#E2E8F0" fontSize="15" fontWeight="800">{station.key}</text>
              <text textAnchor="middle" y="7" fill="#8BA0B5" fontSize="8.5" fontWeight="700">{station.machineType}</text>
              <text textAnchor="middle" y="24" fill={visual.hex} fontSize="8.5" fontWeight="800">{visual.label}</text>
              {qualityTarget && <text textAnchor="middle" y="-45" fill="#F59E0B" fontSize="8" fontWeight="900">OFFSET ACTION</text>}
              {visual.present && <text textAnchor="middle" y="43" fill="#4A5568" fontSize="8">parts {parts} · wip {wip}</text>}
              {visual.alarm && (
                <g transform="translate(48 -27)">
                  <circle r="8" fill="#C0392B" />
                  <text textAnchor="middle" y="3" fill="#fff" fontSize="9" fontWeight="900">!</text>
                </g>
              )}
            </g>
          );
        })}

        {(topology.robots || []).map((robot) => {
          const p = layout.robots[robot.id];
          if (!p) return null;
          const constrained = topology.balance?.constraint === robot.id;
          const robotState = state?.robots?.[robot.id];
          return (
            <g key={robot.id} transform={`translate(${p.x} ${p.y})`} onClick={() => onSelect(robot.id)} style={{ cursor: 'pointer' }}>
              <circle r={constrained ? 51 : 45} fill="rgba(13,27,42,0.95)" stroke={constrained ? '#A89060' : '#4A5568'} strokeWidth={constrained ? 4 : 2} />
              <circle r="29" fill="#1C3557" stroke="#8BA0B5" strokeWidth="1.5" strokeDasharray={robotState?.modelled ? undefined : '4 4'} />
              <text textAnchor="middle" y="-4" fill="#E2E8F0" fontSize="10" fontWeight="800">{robot.id}</text>
              <text textAnchor="middle" y="11" fill={constrained ? '#A89060' : '#8BA0B5'} fontSize="9" fontWeight="700">
                {robot.piecesPerHour} pcs/h
              </text>
              {!robotState?.modelled && <text textAnchor="middle" y="26" fill="#4A5568" fontSize="7.5">STATIC / UNKNOWN</text>}
              {constrained && <text textAnchor="middle" y="-59" fill="#A89060" fontSize="9" fontWeight="900">LINE CONSTRAINT</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
